import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";

const ROLES = [
  { value: "owner", label: "Генеральный директор" },
  { value: "director", label: "Директор" },
  { value: "accountant", label: "Бухгалтер" },
  { value: "viewer", label: "Наблюдатель" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));

export default function Team() {
  const { user } = useAuth();
  const canManage = user?.role === "owner" || user?.role === "director";
  const [members, setMembers] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "accountant" });
  const [delegationForm, setDelegationForm] = useState({ delegate_id: "", start_date: "", end_date: "" });

  const loadMembers = async () => {
    const { data } = await api.get("/team/members");
    setMembers(data);
  };
  const loadDelegations = async () => {
    const { data } = await api.get("/delegations");
    setDelegations(data);
  };

  useEffect(() => {
    loadMembers();
    loadDelegations();
  }, []);

  const addMember = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Заполните имя, email и пароль");
      return;
    }
    try {
      await api.post("/team/members", form);
      toast.success("Сотрудник добавлен в организацию");
      setForm({ name: "", email: "", password: "", role: "accountant" });
      setShowAdd(false);
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось добавить сотрудника");
    }
  };

  const changeRole = async (memberId, role) => {
    try {
      await api.patch(`/team/members/${memberId}`, { role });
      toast.success("Роль обновлена");
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось изменить роль");
    }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/team/members/${memberId}`);
      toast.success("Сотрудник удалён");
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось удалить сотрудника");
    }
  };

  const createDelegation = async () => {
    if (!delegationForm.delegate_id || !delegationForm.start_date || !delegationForm.end_date) {
      toast.error("Заполните все поля делегирования");
      return;
    }
    try {
      await api.post("/delegations", delegationForm);
      toast.success("Право подписи делегировано");
      setDelegationForm({ delegate_id: "", start_date: "", end_date: "" });
      loadDelegations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Не удалось создать делегирование");
    }
  };

  const cancelDelegation = async (id) => {
    try {
      await api.delete(`/delegations/${id}`);
      toast.success("Делегирование отменено");
      loadDelegations();
    } catch {
      toast.error("Не удалось отменить делегирование");
    }
  };

  return (
    <div data-testid="team-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Команда и доступ</h1>
      <p className="text-sm text-slate-500 mb-5">Сотрудники организации, роли и делегирование права подписи</p>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-700">Сотрудники ({members.length})</div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd((v) => !v)} data-testid="add-member-button" className="bg-slate-900 hover:bg-slate-800">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" />Добавить сотрудника
            </Button>
          )}
        </div>

        {showAdd && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
            <Input data-testid="member-name-input" placeholder="Имя" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="member-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="member-password-input" type="password" placeholder="Пароль" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <select data-testid="member-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <Button onClick={addMember} data-testid="member-save-button" className="col-span-2 md:col-span-4 bg-blue-600 hover:bg-blue-700">Сохранить сотрудника</Button>
          </div>
        )}

        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
              <th className="py-2">Имя</th>
              <th className="py-2">Email</th>
              <th className="py-2">Роль</th>
              <th className="py-2 text-right">Действия</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-slate-50" data-testid={`member-row-${m.id}`}>
                <td className="py-2.5 font-medium text-slate-800">{m.name}</td>
                <td className="py-2.5 text-slate-600">{m.email}</td>
                <td className="py-2.5">
                  {canManage ? (
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                      data-testid={`member-role-update-${m.id}`}
                      className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-slate-600">{ROLE_LABEL[m.role]}</span>
                  )}
                </td>
                <td className="py-2.5 text-right">
                  {canManage && m.id !== user?.id && (
                    <Button size="icon" variant="ghost" onClick={() => removeMember(m.id)} data-testid={`remove-member-button-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-sm font-semibold text-slate-700 mb-1">Делегирование права подписи</div>
        <p className="text-xs text-slate-500 mb-4">На время отпуска или болезни назначьте коллегу, который будет подписывать документы от вашего имени</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <select
            value={delegationForm.delegate_id}
            onChange={(e) => setDelegationForm({ ...delegationForm, delegate_id: e.target.value })}
            data-testid="delegation-member-select"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Кому делегировать</option>
            {members.filter((m) => m.id !== user?.id).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <div className="space-y-1.5">
            <Label className="text-xs">С</Label>
            <Input type="date" data-testid="delegation-start-date-input" value={delegationForm.start_date} onChange={(e) => setDelegationForm({ ...delegationForm, start_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">По</Label>
            <Input type="date" data-testid="delegation-end-date-input" value={delegationForm.end_date} onChange={(e) => setDelegationForm({ ...delegationForm, end_date: e.target.value })} />
          </div>
          <Button onClick={createDelegation} data-testid="delegation-save-button" className="self-end bg-blue-600 hover:bg-blue-700">Делегировать</Button>
        </div>

        <div className="space-y-2">
          {delegations.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`delegation-row-${d.id}`}>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-slate-700">
                  <span className="font-medium">{d.delegator_name}</span> → <span className="font-medium">{d.delegate_name}</span>{" "}
                  <span className="text-slate-400">({d.start_date} – {d.end_date})</span>
                </span>
              </div>
              <Button size="icon" variant="ghost" onClick={() => cancelDelegation(d.id)} data-testid={`cancel-delegation-button-${d.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </div>
          ))}
          {delegations.length === 0 && <div className="text-center text-slate-400 text-xs py-6">Активных делегирований нет</div>}
        </div>
      </div>
    </div>
  );
}
